// src/utils/taxCalculations.ts - Utilitaires pour les calculs de TVA

import { TaxRate, TaxCalculation, TaxSummary } from '../types/tax';
import { formatCurrency } from './numberFormat';

/**
 * Calcule les montants HT et TTC à partir d'un prix et d'un taux de TVA
 */
export const calculateTaxAmounts = (
  price: number,
  taxRate: number,
  priceIncludesTax: boolean
): TaxCalculation => {
  let amountExcludingTax: number;
  let amountIncludingTax: number;
  let taxAmount: number;

  if (priceIncludesTax) {
    // Prix TTC saisi -> calculer HT
    amountIncludingTax = price;
    amountExcludingTax = price / (1 + taxRate / 100);
    taxAmount = amountIncludingTax - amountExcludingTax;
  } else {
    // Prix HT saisi -> calculer TTC
    amountExcludingTax = price;
    taxAmount = price * (taxRate / 100);
    amountIncludingTax = amountExcludingTax + taxAmount;
  }

  // Arrondir à 2 décimales
  return {
    originalAmount: Math.round(price * 100) / 100,
    taxRate: taxRate,
    taxAmount: Math.round(taxAmount * 100) / 100,
    amountExcludingTax: Math.round(amountExcludingTax * 100) / 100,
    amountIncludingTax: Math.round(amountIncludingTax * 100) / 100,
  };
};

/**
 * Trouve un taux de TVA par ID dans une liste
 */
export const findTaxRateById = (rates: TaxRate[], taxRateId: string): TaxRate | undefined => {
  return rates.find(rate => rate.id === taxRateId);
};

/**
 * Obtient le taux de TVA par défaut d'une liste
 */
export const getDefaultTaxRate = (rates: TaxRate[]): TaxRate | undefined => {
  return rates.find(rate => rate.isDefault) || rates[0];
};

/**
 * Calcule le résumé TVA pour une liste d'items
 */
export const calculateTaxSummary = (
  items: Array<{
    quantity: number;
    priceExcludingTax: number;
    priceIncludingTax: number;
    taxRateId?: string;
    taxRate?: number;
  }>,
  taxRates: TaxRate[]
): TaxSummary[] => {
  const taxGroups = new Map<string, {
    rate: number;
    name: string;
    baseAmount: number;
    taxAmount: number;
    totalAmount: number;
  }>();

  items.forEach(item => {
    const taxRateId = item.taxRateId || 'default';
    const taxRate = item.taxRate || 0;
    const taxRateInfo = findTaxRateById(taxRates, taxRateId);
    const taxRateName = taxRateInfo?.name || 'TVA';

    const itemBaseAmount = (item.priceExcludingTax || 0) * item.quantity;
    const itemTotalAmount = (item.priceIncludingTax || 0) * item.quantity;
    const itemTaxAmount = itemTotalAmount - itemBaseAmount;

    if (!taxGroups.has(taxRateId)) {
      taxGroups.set(taxRateId, {
        rate: taxRate,
        name: taxRateName,
        baseAmount: 0,
        taxAmount: 0,
        totalAmount: 0,
      });
    }

    const group = taxGroups.get(taxRateId)!;
    group.baseAmount += itemBaseAmount;
    group.taxAmount += itemTaxAmount;
    group.totalAmount += itemTotalAmount;
  });

  return Array.from(taxGroups.entries()).map(([taxRateId, group]) => ({
    taxRateId,
    taxRateName: group.name,
    rate: group.rate,
    baseAmount: Math.round(group.baseAmount * 100) / 100,
    taxAmount: Math.round(group.taxAmount * 100) / 100,
    totalAmount: Math.round(group.totalAmount * 100) / 100,
  }));
};

/**
 * Formate un montant en FCFA
 * Utilise 2 décimales pour conserver les montants décimaux (ex: 234,56).
 * Pour les montants entiers, le séparateur de milliers reste présent.
 */
export const formatAmount = (amount: number | undefined | null): string => {
  return formatCurrency(amount, 2);
};

/**
 * Formate un taux de TVA
 */
export const formatTaxRate = (rate: number): string => {
  return `${rate}%`;
};

/**
 * Validation du prix avec TVA
 */
export const validatePriceWithTax = (
  price: number,
  taxRate: number,
  priceIncludesTax: boolean
): { isValid: boolean; error?: string } => {
  if (!price || price <= 0) {
    return { isValid: false, error: 'Le prix doit être supérieur à 0' };
  }

  if (taxRate < 0 || taxRate > 100) {
    return { isValid: false, error: 'Le taux de TVA doit être entre 0 et 100%' };
  }

  const calculation = calculateTaxAmounts(price, taxRate, priceIncludesTax);
  
  if (calculation.amountExcludingTax <= 0) {
    return { isValid: false, error: 'Le montant HT calculé doit être positif' };
  }

  return { isValid: true };
};

/**
 * Convertit un prix d'un mode à l'autre (HT <-> TTC)
 */
export const convertPriceMode = (
  currentPrice: number,
  taxRate: number,
  fromMode: 'inclusive' | 'exclusive',
  toMode: 'inclusive' | 'exclusive'
): number => {
  if (fromMode === toMode) return currentPrice;

  const calculation = calculateTaxAmounts(currentPrice, taxRate, fromMode === 'inclusive');
  
  return toMode === 'inclusive' 
    ? calculation.amountIncludingTax 
    : calculation.amountExcludingTax;
};